/**
 * # 消行音效 — 16 套和弦方案
 *
 * 每 16 关切换一套和弦，共 16 套（256 / 16），与 BGM 同步切换。 每套包含 5 个和弦（对应 1-5 行消除），每个和弦由 3-5
 * 个音符组成。
 *
 * ## 和弦列表
 *
 * | 关卡段  | 和弦类型 | 特点       |
 * | ------- | -------- | ---------- |
 * | 1-16    | 大三和弦 | 明亮轻快   |
 * | 17-32   | 大二和弦 | 温暖升调   |
 * | 33-48   | 小三和弦 | 蓝调降调   |
 * | 49-64   | 挂留和弦 | 紧张期待   |
 * | 65-80   | 纯五度   | 空灵开放   |
 * | 81-96   | 大七和弦 | 爵士色彩   |
 * | 97-112  | 小七和弦 | 忧郁柔和   |
 * | 113-128 | 增三和弦 | 梦幻全音程 |
 * | 129-144 | 减三和弦 | 不安紧张   |
 * | 145-160 | 挂四和弦 | 悬疑不解决 |
 * | 161-176 | 大六和弦 | 复古温暖   |
 * | 177-192 | 小六和弦 | 神秘冷调   |
 * | 193-208 | 属七和弦 | 推动力强   |
 * | 209-224 | 大九和弦 | 宽广空间感 |
 * | 225-240 | 小九和弦 | 深邃幽暗   |
 * | 241-256 | 加音和弦 | 辉煌终章   |
 *
 * ## 索引说明
 *
 * 每套和弦内部索引对应消除行数（1-5 行）， 实际使用时通过 `Math.min(lines, 4)` 安全索引防止越界。 值为 0 的音符会被
 * `filter((f) => f > 0)` 过滤掉。
 *
 * @constant {number[][][]}
 */
const CHORD_SETS = [
  // 0: 1-16 关 — 大三和弦（明亮）
  [
    [440, 554, 659], // A4 + C#5 + E5
    [587, 740, 880], // D5 + F#5 + A5
    [523, 622, 784], // C5 + Eb5 + G5
    [659, 784, 988], // E5 + G5 + B5
    [440, 659, 880], // A4 + E5 + A5
  ],
  // 1: 17-32 关 — 大二和弦（温暖，升调）
  [
    [494, 622, 740], // B4 + Eb5 + F#5
    [659, 831, 988], // E5 + Ab5 + B5
    [587, 698, 880], // D5 + F5 + A5
    [740, 880, 1109], // F#5 + A5 + C#6
    [494, 740, 988], // B4 + F#5 + B5
  ],
  // 2: 33-48 关 — 小三和弦（蓝调，降调）
  [
    [440, 523, 659], // A4 + C5 + E5
    [587, 698, 880], // D5 + F5 + A5
    [523, 659, 784], // C5 + E5 + G5
    [659, 784, 988], // E5 + G5 + B5
    [440, 659, 880], // A4 + E5 + A5
  ],
  // 3: 49-64 关 — 挂留和弦（紧张）
  [
    [440, 587, 659], // A4 + D5 + E5
    [587, 740, 880], // D5 + F#5 + A5
    [523, 622, 784], // C5 + Eb5 + G5
    [659, 784, 988], // E5 + G5 + B5
    [440, 659, 880], // A4 + E5 + A5
  ],
  // 4: 65-80 关 — 纯五度（空灵）
  [
    [440, 659, 0], // A4 + E5（两音）
    [587, 880, 0], // D5 + A5（两音）
    [523, 784, 0], // C5 + G5（两音）
    [659, 988, 0], // E5 + B5（两音）
    [440, 880, 0], // A4 + A5（八度）
  ],
  // 5: 81-96 关 — 大七和弦（爵士）
  [
    [440, 554, 659, 831], // A4 + C#5 + E5 + Ab5
    [587, 740, 880, 1109], // D5 + F#5 + A5 + C#6
    [523, 622, 784, 988], // C5 + Eb5 + G5 + B5
    [659, 784, 988, 1245], // E5 + G5 + B5 + Eb6
    [440, 659, 880, 1109], // A4 + E5 + A5 + C#6
  ],
  // 6: 97-112 关 — 小七和弦（忧郁）
  [
    [440, 523, 659, 784], // A4 + C5 + E5 + G5
    [587, 698, 880, 1047], // D5 + F5 + A5 + C6
    [523, 622, 784, 932], // C5 + Eb5 + G5 + Bb5
    [659, 784, 988, 1175], // E5 + G5 + B5 + D6
    [440, 659, 880, 1047], // A4 + E5 + A5 + C6
  ],
  // 7: 113-128 关 — 增三和弦（梦幻）
  [
    [440, 554, 670], // A4 + C#5 + E#5（≈F5）
    [587, 740, 900], // D5 + F#5 + A#5（≈Bb5）
    [523, 659, 800], // C5 + E5 + G#5（≈Ab5）
    [659, 831, 1000], // E5 + Ab5 + C6
    [440, 659, 840], // A4 + E5 + A#5（≈Bb5）
  ],
  // 8: 129-144 关 — 减三和弦（不安）
  [
    [440, 523, 622], // A4 + C5 + Eb5
    [587, 698, 831], // D5 + F5 + Ab5
    [523, 622, 740], // C5 + Eb5 + F#5
    [659, 784, 932], // E5 + G5 + Bb5
    [440, 622, 831], // A4 + Eb5 + Ab5
  ],
  // 9: 145-160 关 — 挂四和弦（悬疑）
  [
    [440, 587, 698], // A4 + D5 + F5
    [587, 784, 932], // D5 + G5 + Bb5
    [523, 698, 880], // C5 + F5 + A5
    [659, 880, 1047], // E5 + A5 + C6
    [440, 698, 932], // A4 + F5 + Bb5
  ],
  // 10: 161-176 关 — 大六和弦（复古）
  [
    [440, 554, 659, 740], // A4 + C#5 + E5 + F#5
    [587, 740, 880, 988], // D5 + F#5 + A5 + B5
    [523, 622, 784, 880], // C5 + Eb5 + G5 + A5
    [659, 784, 988, 1109], // E5 + G5 + B5 + C#6
    [440, 659, 880, 988], // A4 + E5 + A5 + B5
  ],
  // 11: 177-192 关 — 小六和弦（神秘）
  [
    [440, 523, 659, 740], // A4 + C5 + E5 + F#5
    [587, 698, 880, 988], // D5 + F5 + A5 + B5
    [523, 622, 784, 880], // C5 + Eb5 + G5 + A5
    [659, 784, 988, 1109], // E5 + G5 + B5 + C#6
    [440, 659, 880, 988], // A4 + E5 + A5 + B5
  ],
  // 12: 193-208 关 — 属七和弦（推动感）
  [
    [440, 554, 659, 784], // A4 + C#5 + E5 + G5
    [587, 740, 880, 1047], // D5 + F#5 + A5 + C6
    [523, 622, 784, 932], // C5 + Eb5 + G5 + Bb5
    [659, 784, 988, 1175], // E5 + G5 + B5 + D6
    [440, 659, 880, 1047], // A4 + E5 + A5 + C6
  ],
  // 13: 209-224 关 — 大九和弦（宽广）
  [
    [440, 554, 659, 831, 988], // A4 + C#5 + E5 + Ab5 + B5
    [587, 740, 880, 1109, 1319], // D5 + F#5 + A5 + C#6 + E6
    [523, 622, 784, 988, 1175], // C5 + Eb5 + G5 + B5 + D6
    [659, 784, 988, 1245, 1480], // E5 + G5 + B5 + Eb6 + F#6
    [440, 659, 880, 1109, 1319], // A4 + E5 + A5 + C#6 + E6
  ],
  // 14: 225-240 关 — 小九和弦（深邃）
  [
    [440, 523, 659, 784, 932], // A4 + C5 + E5 + G5 + Bb5
    [587, 698, 880, 1047, 1245], // D5 + F5 + A5 + C6 + Eb6
    [523, 622, 784, 932, 1109], // C5 + Eb5 + G5 + Bb5 + C#6
    [659, 784, 988, 1175, 1397], // E5 + G5 + B5 + D6 + F6
    [440, 659, 880, 1047, 1245], // A4 + E5 + A5 + C6 + Eb6
  ],
  // 15: 241-256 关 — 加音和弦（辉煌）
  [
    [440, 554, 659, 784, 880], // A4 + C#5 + E5 + G5 + A5
    [587, 740, 880, 1047, 1175], // D5 + F#5 + A5 + C6 + D6
    [523, 622, 784, 932, 1047], // C5 + Eb5 + G5 + Bb5 + C6
    [659, 784, 988, 1175, 1319], // E5 + G5 + B5 + D6 + E6
    [440, 659, 880, 1109, 1319], // A4 + E5 + A5 + C#6 + E6
  ],
];

export default CHORD_SETS;
